import { AbortError } from '@lingoda/errors';

/**
 * Call fetch with retry mechanism. It will retry the request if it fails due to network issues or if the response is not ok (status code is not in the range 200-299).
 * @returns Successful response from fetch in case of success, otherwise it throws either a Response object (in case of non-ok response) or an error (in case of network failure).
 */
export const fetchWithRetry = async ({
    url,
    options,
    retries = 3,
    backoff = 1000,
    onRequestFailure,
}: {
    url: string;
    options: RequestInit;
    retries?: number;
    backoff?: number;
    onRequestFailure?: (url: string, options: RequestInit) => Promise<void>;
}): Promise<Response> => {
    try {
        const response = await fetch(url, options);
        if (!response.ok) {
            throw response;
        }

        return response;
    } catch (error) {
        if (options.signal?.aborted) {
            throw new AbortError({
                message: 'Fetching URL was aborted',
                context: {
                    request: {
                        url,
                        body: options.body?.toString(),
                    },
                },
            });
        }

        if (retries === 0 || !navigator.onLine) {
            await onRequestFailure?.(url, options);

            throw error;
        }

        await new Promise((resolve) => setTimeout(resolve, backoff));

        return fetchWithRetry({
            url,
            options,
            retries: retries - 1,
            backoff: backoff * 2,
            onRequestFailure,
        });
    }
};
